import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from '../_shared/shared.service';
import { Compendium } from './compendium';

@Injectable({ providedIn: 'root' })
export class CompendiumService {

	statuses: any = [
		{ value: '0', name: 'Active' },
		{ value: '2', name: 'Suspended' },
		{ value: '1', name: 'Canceled' }
	];

	filters: any = [
		{ value: 'propertyCode', name: 'Property Code' },
		{ value: 'adrCity', name: 'City' },
		{ value: 'adrState', name: 'State' },
		{ value: 'adrCounty', name: 'County' },
	];

	// Matched case-insensitively by the backend cascade query.
	locationTypes: any = [
		{ value: 'Policy', name: 'Policy (applies to all units)' },
		{ value: 'Location', name: 'Location (applies to all units at a location)' },
		{ value: 'Development', name: 'Development (applies to all units at a development)' },
		{ value: 'Unit', name: 'Unit (applies only to assigned units)' },
	];

	locations: any = [
		{ value: 1, name: 'Maui' },
		{ value: 2, name: 'Park City' },
		{ value: 3, name: 'Big Island' },
		{ value: 4, name: 'Oahu' },
	];

	locationAreas: any = [
		{ value: 'Arrival Information', name: 'Arrival Information', order: 1 },
		{ value: 'In-House Contacts', name: 'In-House Contacts', order: 2 },
		{ value: 'Parking', name: 'Parking', order: 3 },
		{ value: 'Rules', name: 'Rules', order: 4 },
		{ value: 'Garage', name: 'Garage', order: 5 },
		{ value: 'Entrance', name: 'Entrance', order: 6 },
		{ value: 'Living Room', name: 'Living Room', order: 7 },
		{ value: 'Kitchen', name: 'Kitchen', order: 8 },
		{ value: 'Entertainment', name: 'Entertainment', order: 9 },
		{ value: 'Family Room', name: 'Family Room', order: 10 },
		{ value: 'Laundry', name: 'Laundry', order: 11 },
		{ value: 'Balcony', name: 'Balcony', order: 12 },
		{ value: 'Mudroom', name: 'Mudroom', order: 13 },
		{ value: 'Dining', name: 'Dining', order: 14 },
		{ value: 'Den', name: 'Den', order: 15 },
		{ value: 'Patio', name: 'Patio', order: 16 },
		{ value: 'Master Bed', name: 'Master Bed', order: 17 },
		{ value: 'Master Bath', name: 'Master Bath', order: 18 },
		{ value: 'Guest Bed 1', name: 'Guest Bed 1', order: 19 },
		{ value: 'Guest Bath 1', name: 'Guest Bath 1', order: 20 },
		{ value: 'Guest Bed 2', name: 'Guest Bed 2', order: 21 },
		{ value: 'Guest Bath 2', name: 'Guest Bath 2', order: 22 },
		{ value: 'Guest Bed 3', name: 'Guest Bed 3', order: 23 },
		{ value: 'Guest Bath 3', name: 'Guest Bath 3', order: 24 },
		{ value: 'Guest Bed 4', name: 'Guest Bed 4', order: 25 },
		{ value: 'Guest Bath 4', name: 'Guest Bath 4', order: 26 },
		{ value: 'Guest Bed 5', name: 'Guest Bed 5', order: 27 },
		{ value: 'Guest Bath 5', name: 'Guest Bath 5', order: 28 },
		{ value: 'Guest Bed 6', name: 'Guest Bed 6', order: 29 },
		{ value: 'Guest Bath 6', name: 'Guest Bath 6', order: 30 },
		{ value: 'Guest Bed 7', name: 'Guest Bed 7', order: 31 },
		{ value: 'Guest Bath 7', name: 'Guest Bath 7', order: 32 },
		{ value: 'Guest Bed 8', name: 'Guest Bed 8', order: 33 },
		{ value: 'Guest Bath 8', name: 'Guest Bath 8', order: 34 },
		{ value: 'Guest Bed 9', name: 'Guest Bed 9', order: 35 },
		{ value: 'Guest Bath 9', name: 'Guest Bath 9', order: 36 },
		{ value: 'Fitness', name: 'Fitness', order: 37 },
		{ value: 'Amentities', name: 'Amentities', order: 38 },
		{ value: 'Activities', name: 'Activities', order: 39 },
		{ value: 'Retail', name: 'Retail', order: 40 },
		{ value: 'Restaurants', name: 'Restaurants', order: 41 },
		{ value: 'Billiard room', name: 'Billiard room', order: 42 },
		{ value: 'Indoor pool', name: 'Indoor pool', order: 43 },
		{ value: 'Departure Infrormation', name: 'Departure Infrormation', order: 44 },
	];

	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'compendiums';

	add(compendium: Compendium): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(compendium));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, compendium);
	}

	update(compendium: Compendium): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, compendium);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/find-all`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findAllCompendiums(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/find-all`).pipe(retry({ count: 2, delay: 1500 }));
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
